import React, { useCallback } from "react"
import { StatusPill } from "./StatusPill"
import { formatFileSize } from "../utils"

/**
 * @typedef {Object} UploadedFile
 * @property {string} id - File identifier
 * @property {string} filename - Display filename
 * @property {number} file_size_bytes - File size in bytes
 * @property {string} placement_status - Status: pending, placed, out_of_scope
 * @property {string|null} created_at - ISO timestamp of upload
 */

/**
 * Single file row with two-zone flex layout.
 * Left zone: checkbox + filename. Right zone: size, date, status pill, actions.
 *
 * @param {Object} props
 * @param {UploadedFile} props.file - The uploaded file data
 * @param {boolean} props.isSelected - Whether this row's checkbox is checked
 * @param {Function} props.onSelect - Callback when checkbox toggled, receives file ID
 * @param {Function} props.onStatusChange - Callback when status changes, receives (fileId, newStatus)
 * @param {Function} props.onDownload - Callback when download clicked, receives file ID
 * @returns {React.ReactElement} The file row component
 */
export function FileRow({ file, isSelected, onSelect, onStatusChange, onDownload }) {
  const handleCheckbox = useCallback(() => {
    onSelect(file.id)
  }, [file.id, onSelect])

  const handleStatusChange = useCallback((newStatus) => {
    onStatusChange(file.id, newStatus)
  }, [file.id, onStatusChange])

  const handleDownload = useCallback(() => {
    onDownload(file.id)
  }, [file.id, onDownload])

  const className = `file-row${isSelected ? " selected" : ""}`

  return (
    <div className={className}>
      <div className="file-row-left">
        <input
          type="checkbox"
          className="file-checkbox"
          checked={isSelected}
          onChange={handleCheckbox}
          aria-label={`Select ${file.filename}`}
        />
        <span className="file-row-filename">{file.filename}</span>
        <span className="file-row-size">{formatFileSize(file.file_size_bytes)}</span>
        <StatusPill
          status={file.placement_status}
          onStatusChange={handleStatusChange}
        />
      </div>
      <div className="file-row-actions">
        <button
          className="file-row-action-btn"
          onClick={handleDownload}
          aria-label="Download"
          type="button"
        >
          ↓
        </button>
      </div>
    </div>
  )
}
